import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CircularProgress,
    makeStyles
} from '@mui/material';
import { makeStyles as makeStylesMUI } from '@mui/styles';
import { Hotel, Person, DirectionsCar, Event as EventIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { persistentStorage } from '../utils/storage';
import { markSessionStart } from '../utils/session';


const useStyles = makeStylesMUI((theme) => ({
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'white',
        padding: '16px',
        fontFamily: 'Nunito, Poppins, Sansation, sans-serif',
    },
    card: {
        width: '100%',
        maxWidth: 400,
        padding: '32px',
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        textAlign: 'center'
    },
    header: {
        color: '#C084FC',
        fontSize: '24px',
        fontFamily: 'Poppins',
        fontWeight: '700',
        marginBottom: '8px'
    },
    subheader: {
        color: '#666',
        fontSize: '16px',
        fontFamily: 'Nunito',
        marginBottom: '32px'
    },
    hotelCard: {
        marginBottom: '16px',
        cursor: 'pointer',
        border: '2px solid transparent',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        '&:hover': {
            borderColor: '#C084FC',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(192, 132, 252, 0.15)'
        }
    },
    selectedHotel: {
        borderColor: '#C084FC',
        backgroundColor: 'rgba(192, 132, 252, 0.05)'
    },
    hotelName: {
        fontSize: '18px',
        fontFamily: 'Poppins',
        fontWeight: '600',
        color: '#333',
        marginBottom: '4px'
    },
    roleText: {
        fontSize: '14px',
        fontFamily: 'Nunito',
        color: '#666',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
    },
    continueButton: {
        width: '100%',
        height: '48px',
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        color: 'white',
        fontSize: '16px',
        fontFamily: 'Nunito',
        fontWeight: '600',
        borderRadius: '12px',
        textTransform: 'none',
        marginTop: '24px',
        '&:hover': {
            background: 'linear-gradient(90deg, #A066E8 0%, #5A3A7A 100%)'
        },
        '&:disabled': {
            background: '#E0E0E0',
            color: '#999'
        }
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
    },
    logoutButton: {
        width: '100%',
        height: '44px',
        color: '#724F96',
        fontSize: '15px',
        fontFamily: 'Nunito',
        fontWeight: '600',
        borderRadius: '12px',
        textTransform: 'none',
        marginTop: '12px',
        '&:hover': {
            backgroundColor: 'rgba(192, 132, 252, 0.08)'
        }
    }
}));

const HotelSelection = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Get data from route state
    const { userData, hotels: allHotels, requireReset } = location.state || {};
    
    // Filter out customer roles - only show associate roles (manager, supervisor, valet, serviceboy, time_office_supervisor)
    const ASSOCIATE_ROLES = ['manager', 'supervisor', 'valet', 'serviceboy', 'time_office_supervisor'];
    // Latest-created events first so same-named events are tellable apart;
    // hotel/property entries (no event_created_at) sink to the bottom.
    const hotels = allHotels
        ? allHotels
            .filter(hotel => ASSOCIATE_ROLES.includes(hotel.role))
            .sort((a, b) => (b.event_created_at || '').localeCompare(a.event_created_at || ''))
        : [];
    
    // Debug logging
    console.log('[HotelSelection] Location state:', location.state);
    console.log('[HotelSelection] UserData:', userData);
    console.log('[HotelSelection] All Hotels (before filter):', allHotels);
    console.log('[HotelSelection] Hotels (after filter):', hotels);
    console.log('[HotelSelection] RequireReset:', requireReset);

    const entryKey = (e) => `${e.user_id}-${e.hotel_id ?? 'null'}-${e.event_id ?? 'null'}`;

    const formatEventDate = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const handleHotelSelect = (hotel) => {
        setSelectedHotel(hotel);
    };

    const handleLogout = async () => {
        try {
            if (userData?.user_id) {
                await axios.patch(`${config.API_BASE_URL}/user-status/logout`, {
                    user_id: userData.user_id,
                });
            }
        } catch (err) {
            console.error('[HotelSelection] Logout API error:', err);
        } finally {
            try { await persistentStorage.clear?.(); } catch (_) { /* ignore */ }
            try { localStorage.clear(); } catch (_) { /* native build */ }
            navigate('/associates-login', { replace: true });
        }
    };

    const handleContinue = async () => {
        if (!selectedHotel) return;

        setIsLoading(true);

        console.log('[HotelSelection] handleContinue start, selectedHotel:', selectedHotel);

        try {
            // Store the selected hotel and user data
            localStorage.setItem('currentUserName', userData.name);
            if (userData.designation) localStorage.setItem('currentUserNickname', userData.designation);
            else localStorage.removeItem('currentUserNickname');
            localStorage.setItem('currentRole', selectedHotel.role);
            localStorage.setItem('currentUserEmail', userData.email);
            localStorage.setItem('hotel_id', selectedHotel.hotel_id);
            localStorage.setItem('currentUserPhone', userData.phone);
            localStorage.setItem('currentUserId', selectedHotel.user_id);
            localStorage.setItem('currentUserFirstName', userData.first_name);
            // Also persist for ProtectedRoute (mobile/web TTL)
            const currentUserObj = {
                user_id: selectedHotel.user_id,
                name: userData.name,
                phone: userData.phone,
                email: userData.email || null,
                role: selectedHotel.role,
                hotel_id: selectedHotel.hotel_id,
                verification_status: selectedHotel.verification_status
            };
            await persistentStorage.set('currentUserName', userData.name || '');
            await persistentStorage.set('currentUserNickname', userData.designation || null);
            await persistentStorage.set('currentRole', selectedHotel.role);
            await persistentStorage.set('currentUserEmail', userData.email || '');
            if (selectedHotel.hotel_id != null) {
                await persistentStorage.set('hotel_id', String(selectedHotel.hotel_id));
                localStorage.setItem('hotel_id', String(selectedHotel.hotel_id));
            } else {
                await persistentStorage.remove('hotel_id');
                localStorage.removeItem('hotel_id');
            }
            if (selectedHotel.event_id) {
                await persistentStorage.set('currentEventId', String(selectedHotel.event_id));
                localStorage.setItem('currentEventId', String(selectedHotel.event_id));
            } else {
                await persistentStorage.remove('currentEventId');
                localStorage.removeItem('currentEventId');
            }
            await persistentStorage.set('currentUserPhone', userData.phone || '');
            await persistentStorage.set('currentUserId', String(selectedHotel.user_id));
            await persistentStorage.set('currentUser', JSON.stringify({ ...currentUserObj, event_id: selectedHotel.event_id || null }));
            await markSessionStart();

            // Create session for the selected hotel's user. Pass the workspace
            // IDs the user just picked so the backend broadcast doesn't fan out
            // to other rooms (event ↔ property leak when a pool valet is
            // attached to both — see /user-status/login _scope_from_request_or_user).
            console.log('[HotelSelection] Creating session for user_id:', selectedHotel.user_id);
            await axios.post(`${config.API_BASE_URL}/user-status/login`, {
                user_id: selectedHotel.user_id,
                hotel_id: selectedHotel.hotel_id || null,
                event_id: selectedHotel.event_id || null,
                valet_service_id: selectedHotel.valet_service_id || null,
            });
            console.log('[HotelSelection] Session created successfully');

            // Handle password reset flow
            if (requireReset) {
                navigate('/reset-password', {
                    state: {
                        userId: selectedHotel.user_id,
                        fromAssociates: true
                    }
                });
                return;
            }

            // Navigate based on role and verification status
            console.log('[HotelSelection] Selected hotel details:', selectedHotel);
            console.log('[HotelSelection] Selected hotel first_name:', selectedHotel.first_name);
            console.log('[HotelSelection] Selected hotel verification_status:', selectedHotel.verification_status);
            
            // Manager → manager dashboard
            if (selectedHotel.role === 'manager') {
                console.log('[HotelSelection] Manager - navigating to manager dashboard');
                await markSessionStart();
                navigate('/manager-dashboard');
                return;
            }

            // Time Office Supervisor — both property AND event scopes land on
            // the dedicated /tos-dashboard. TosDashboard reads currentEventId
            // and hotel_id from persistentStorage (set above) to figure out
            // which scope to render, so a single route handles both. The
            // earlier event-scope branch routed to /event-organizer/events/:id
            // (the EO event dashboard), which dropped TOS operational features
            // (employee management, parking config, realtime tickets) — the
            // user reported "work isn't there in event dashboard".
            if (selectedHotel.role === 'time_office_supervisor') {
                console.log('[HotelSelection] Time Office Supervisor - navigating to tos dashboard');
                await markSessionStart();
                navigate('/tos-dashboard');
                return;
            }

            else if (selectedHotel.role === 'supervisor') {
                if (selectedHotel.verification_status === 'verified') {
                    console.log('[HotelSelection] User is verified, navigating to supervisor dashboard');
                    await markSessionStart();
                    navigate('/supervisor-dashboard');
                } else {
                    // Check if user has submitted personal details for THIS specific hotel
                    if (selectedHotel.first_name && selectedHotel.first_name.trim() !== '') {
                        console.log('[HotelSelection] User has submitted personal details for this hotel, showing verification pending');
                        // User has submitted personal details for this hotel, show verification pending page
                        navigate('/verification-pending', {
                            state: {
                                userData: { ...userData, ...selectedHotel },
                                role: selectedHotel.role
                            }
                        });
                    } else {
                        console.log('[HotelSelection] User has NOT submitted personal details for this hotel, redirecting to personal details form');
                        // User hasn't submitted personal details for this hotel, redirect to personal details form
                        navigate('/personal-details', {
                            state: {
                                userData: { ...userData, ...selectedHotel },
                                role: selectedHotel.role
                            }
                        });
                    }
                }
            } else if (selectedHotel.role === 'valet' || selectedHotel.role === 'serviceboy') {
                if (selectedHotel.verification_status === 'verified') {
                    console.log('[HotelSelection] User is verified, navigating to valet dashboard');
                    navigate('/valet-dashboard');
                } else {
                    // Check if user has submitted personal details for THIS specific hotel
                    if (selectedHotel.first_name && selectedHotel.first_name.trim() !== '') {
                        console.log('[HotelSelection] User has submitted personal details for this hotel, showing verification pending');
                        // User has submitted personal details for this hotel, show verification pending page
                        navigate('/verification-pending', {
                            state: {
                                userData: { ...userData, ...selectedHotel },
                                role: selectedHotel.role
                            }
                        });
                    } else {
                        console.log('[HotelSelection] User has NOT submitted personal details for this hotel, redirecting to personal details form');
                        // User hasn't submitted personal details for this hotel, redirect to personal details form
                        navigate('/personal-details', {
                            state: {
                                userData: { ...userData, ...selectedHotel },
                                role: selectedHotel.role
                            }
                        });
                    }
                }
            }
            else {
                console.warn('[HotelSelection] Unhandled role, falling back to /dashboard:', selectedHotel.role);
                navigate('/dashboard');
            }
        } catch (error) {
            console.error('[HotelSelection] handleContinue error:', error, error?.stack);
            alert(`Login navigation failed: ${error?.message || error}`);
        } finally {
            setIsLoading(false);
        }
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'supervisor':
                return <Person sx={{ fontSize: 20, color: '#C084FC' }} />;
            case 'valet':
                return <DirectionsCar sx={{ fontSize: 20, color: '#C084FC' }} />;
            default:
                return <Hotel sx={{ fontSize: 20, color: '#C084FC' }} />;
        }
    };

    const getRoleText = (role) => {
        switch (role) {
            case 'supervisor':
                return 'Supervisor';
            case 'time_office_supervisor':
                return 'Time Office Supervisor';
            case 'valet':
                return 'Valet';
            default:
                return 'Employee';
        }
    };

    // Handle missing data
    if (!userData || !hotels || hotels.length === 0) {
        return (
            <div className={classes.container}>
                <Box className={classes.card}>
                    <Typography className={classes.header}>
                        No Hotels Found
                    </Typography>
                    <Typography className={classes.subheader}>
                        You are not associated with any hotels. Please contact your administrator.
                    </Typography>
                    <Button
                        className={classes.continueButton}
                        onClick={() => navigate('/associates-login')}
                    >
                        Back to Login
                    </Button>
                </Box>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={classes.container}>
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Box className={classes.card}>
                <Typography className={classes.header}>
                    Select Hotel
                </Typography>
                <Typography className={classes.subheader}>
                    Choose which hotel you want to work at today
                </Typography>

                {hotels.map((hotel) => {
                    const key = entryKey(hotel);
                    const isSelected = selectedHotel && entryKey(selectedHotel) === key;
                    const isEvent = !!hotel.event_id;
                    return (
                        <Card
                            key={key}
                            className={`${classes.hotelCard} ${isSelected ? classes.selectedHotel : ''}`}
                            onClick={() => handleHotelSelect(hotel)}
                        >
                            <CardContent sx={{ padding: '16px' }}>
                                <Typography
                                    sx={{
                                        fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                                        color: isEvent ? '#724F96' : '#888', mb: 0.5,
                                    }}
                                >
                                    {isEvent ? 'EVENT' : 'HOTEL'}
                                </Typography>
                                <Typography className={classes.hotelName}>
                                    {hotel.hotel_name}
                                </Typography>
                                {isEvent && (hotel.event_start_time || hotel.address_line) && (
                                    <Typography
                                        sx={{
                                            fontSize: 13, fontFamily: 'Nunito', color: '#888',
                                            mb: 0.5, lineHeight: 1.4,
                                        }}
                                    >
                                        {[formatEventDate(hotel.event_start_time), hotel.address_line]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </Typography>
                                )}
                                <Typography className={classes.roleText}>
                                    {isEvent ? <EventIcon sx={{ fontSize: 20, color: '#C084FC' }} /> : getRoleIcon(hotel.role)}
                                    {getRoleText(hotel.role)}
                                </Typography>
                            </CardContent>
                        </Card>
                    );
                })}

                <Button
                    className={classes.continueButton}
                    onClick={handleContinue}
                    disabled={!selectedHotel}
                >
                    Continue
                </Button>

                <Button
                    className={classes.logoutButton}
                    onClick={handleLogout}
                    startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
                >
                    Logout
                </Button>
            </Box>
        </div>
    );
};

export default HotelSelection;
